import React, { useEffect, useRef, useState } from 'react';
import Pagination from './Pagination';
import { logTone, logLabel } from '../clickLog';
import { formatUtcDateTime } from '../dateTime';

const BLOCKED_LOG = /block|not trusted|not found|danger|app closed/i;

const MONO_COLUMNS = new Set([
  'deviceID',
  'androidId',
  'adId',
  'idfa',
  'idfv',
  'ip',
  'pushToken',
  'clickTime',
  'app_version',
  'appVersion',
  'osVersion',
]);

const formatValue = (value) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '—';
    }
  }
  return String(value);
};

const formatTime = (value) => formatUtcDateTime(value);

const cellWidth = (col) => Math.min(Math.max(col.minWidth || 140, 90), 230);

function EmptyMark() {
  return (
    <span className="empty-mark" aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  );
}

function ColumnsMenu({ columns, visibleColumns, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div className="columns-menu" ref={boxRef}>
      <button type="button" className="columns-menu-btn" onClick={() => setOpen((v) => !v)}>
        Колонки
        <span className="columns-menu-count">{visibleColumns.length}</span>
      </button>

      {open ? (
        <div className="columns-menu-pop">
          <div className="columns-menu-head">
            <span>Показувати</span>
            <button type="button" className="columns-menu-reset" onClick={onReset}>
              Скинути
            </button>
          </div>
          <div className="columns-menu-list">
            {columns.map((col) => (
              <label key={col.key} className="columns-menu-item">
                <input
                  type="checkbox"
                  checked={visibleColumns.includes(col.key)}
                  onChange={() => onToggle(col.key)}
                />
                <span>{col.label}</span>
              </label>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SkeletonTable({ columns }) {
  return (
    <div className="app-editor-card compact-card mt-3">
      <div className="app-editor-card-body compact-card-body">
        <div className="stats-section-title mb-3">Loading clicks…</div>
        <div className="skeleton-table">
          {Array.from({ length: 10 }).map((_, row) => (
            <div className="skeleton-row" key={row}>
              {columns.slice(0, 7).map((col) => (
                <span className="skeleton-cell" key={col.key} style={{ width: `${cellWidth(col) / 2}px` }} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ClickStats({
  clicks,
  onRowClick,
  loading,
  page,
  pageSize,
  totalPages,
  total,
  onPageChange,
  onPageSizeChange,
  columns,
  visibleColumns,
  onToggleColumn,
  onResetColumns,
}) {
  const activeColumns = columns.filter((col) => visibleColumns.includes(col.key));

  if (loading && (!Array.isArray(clicks) || clicks.length === 0)) {
    return <SkeletonTable columns={activeColumns} />;
  }

  if (!Array.isArray(clicks) || clicks.length === 0) {
    return (
      <div className="app-editor-card compact-card mt-3">
        <div className="app-editor-card-body compact-card-body">
          <div className="stats-empty-state stats-empty-state-lg">
            <EmptyMark />
            <div className="stats-empty-title">Кліків не знайдено</div>
            <div className="stats-empty-text">Спробуйте інший період або скиньте фільтри.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-editor-card compact-card mt-3">
      <div className="app-editor-card-body compact-card-body">
        <div className="clicks-head">
          <div className="stats-section-subtitle">
            Сторінка {page} з {totalPages}
          </div>

          <ColumnsMenu
            columns={columns}
            visibleColumns={visibleColumns}
            onToggle={onToggleColumn}
            onReset={onResetColumns}
          />
        </div>

        <div className="clicks-scroll">
          <table className="clicks-table">
            <thead>
              <tr>
                {activeColumns.map((col) => (
                  <th key={col.key} style={{ width: cellWidth(col) }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {clicks.map((click, index) => (
                <tr
                  key={`${click.deviceID || 'no-device'}_${click.clickTime || 'no-time'}_${index}`}
                  className={BLOCKED_LOG.test(click.clickLog || '') ? 'is-blocked' : undefined}
                >
                  {activeColumns.map((col) => {
                    const raw = click[col.key];
                    const text =
                      col.key === 'clickTime'
                        ? formatTime(raw)
                        : col.key === 'clickLog'
                          ? logLabel(raw)
                          : formatValue(raw);

                    return (
                      <td key={col.key}>
                        {col.key === 'clickTime' ? (
                          <button
                            type="button"
                            className="cell-link is-mono"
                            style={{ maxWidth: cellWidth(col) }}
                            onClick={() => onRowClick && onRowClick(click)}
                          >
                            {text}
                          </button>
                        ) : (
                          <span
                            className={`cell-clip${MONO_COLUMNS.has(col.key) ? ' is-mono' : ''}`}
                            style={{ maxWidth: cellWidth(col) }}
                            title={formatValue(raw)}
                          >
                            {col.key === 'clickLog' ? (
                              <>
                                <span className={`click-log-dot is-${logTone(raw)}`} />
                                {text}
                              </>
                            ) : (
                              text
                            )}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="clicks-foot">
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={[50, 100, 250, 500, 1000]}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
}
